import { useEffect } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import LZString from "lz-string";

import UFOSegment from "@atlaskit/react-ufo/segment";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import {
    WacAnalyticsClientProvider,
    WacAnalyticsListener
} from "@/analytics/wac-analytics";
import { SitesContextProvider } from "@/components/sites-provider";
import { UserContextProvider } from "@/components/user-context";
import { GupsProvider } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";

import { InitializeAutoAtlAnalytics } from "./analytics/atl-analytics";
import { Identity } from "./analytics/atl-analytics/identity";
import { InitUFOV1 } from "./analytics/ufo/initUFO";
import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { CrossFlowProvider } from "./components/cross-flow";
import { AppPricingData } from "./contentful";
import {
    AvailableProductsResponse,
    BxpWebStargateClientProvider,
    CcpTransactionAccountData,
    getStargateBaseUrl,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src";
import { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client";
import { AuxiaResponse } from "./pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment";
import { trackRouteUFOV1PageLoad } from "./ufo-v1";
import { UserSegmentTypes } from "./uniform/components/personalization-container/src/use-user-segments";
import { getUniformEnvironment } from "./uniform/utils";
import { createUniformContext } from "./uniform/utils/uniform-context";
import {
    ContentfulEnvironment,
    WACEnvironment,
    WAEContextProvider
} from "./uniform/utils/wae-context-provider";
import { Cookie } from "./utils";
import { checkFeatureFlagGate, featureFlags } from "./utils/feature-flags";
import { GridOverlayWrapper } from "./utils/grid-overlay";
import { useAnchorLocalStorage } from "./utils/local-storage-hook";
import { LocationProvider } from "./utils/location-provider";
import { LoggedInUserProvider } from "./utils/logged-in-user-provider";
import { OneTrustProvider } from "./utils/onetrust-provider";
import { PricingContent } from "./utils/pricing-queries/get-pricing-content";
import { SmartReCaptcha } from "./utils/smart-recaptcha";
import { SSRLoginStateProvider } from "./utils/ssr-login-state-provider";
import { useDynamicTitleDescription } from "./utils/use-dynamic-title-description";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

import type {
    Template,
    TemplateCollection
} from "./content/templates/template.types";
import type { PreloadedQuery } from "react-relay";
import type { OperationType } from "relay-runtime";

export type CompositionMetrics = {
    compositionFetchStart?: number;
    compositionFetchEnd?: number;
    compositionCacheStatus?: string;
    ssrEndTime?: number;
};

export type PageProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    agileArticle?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    agileOverview?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tutorial?: any;
    auxiaResponse?: AuxiaResponse | null;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    loggedInContext?: GrowthUnifiedProfile;
    loggedInContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    templates?: Template[];
    partnerTemplates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    pricingContent?: PricingContent;
    teamType?: string;
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    isAllowPostMessage?: boolean;
    keyword?: string;
    mlTemplateIds?: string[];
    anchor?: string;
    subAnchor?: string;
    referrer?: string;
    track?: string;
    queryRef?: PreloadedQuery<OperationType>;
    hasCloudSessionTokenCookie?: boolean;
    transactionAccounts?: Array<CcpTransactionAccountData>;
    compositionMetrics?: CompositionMetrics;
    rawCompositionFile?: unknown;
    clusterTag?: string;
    utmMedium?: string;
    utmContent?: string;
    utmCampaign?: string;
    compositionStatusCode?: number;
};

export type PageWrapperProps = PageProps & {
    PageComponent: React.ComponentType<PageProps>;
    analyticsWebClient?: AnalyticsWebClient | null;
    location: Location;
    userAgent?: string;
};

/* Temporary code for debugging purposes - will be removed */
function getExperimentKeyValue(query: string, key: string): string | null {
    if (!query.startsWith("?")) {
        query = "?" + query;
    }

    const params = new URLSearchParams(query);
    return params.get(key);
}

const X_EXPERIMENTS_KEY = "X-Experiments-Key";

const checkEdgeCacheKeyMismatch = (
    analyticsWebClient: AnalyticsWebClient | null | undefined,
    wacWebTrafficAAGateValue: boolean
) => {
    if (analyticsWebClient) {
        const xExperimentsKey = Cookie.readCookie(X_EXPERIMENTS_KEY);
        if (xExperimentsKey) {
            const splitKey = xExperimentsKey.split("~")[1];
            if (!splitKey) return;
            const decodedExperimentsKey =
                LZString.decompressFromBase64(splitKey);
            const evaluatedValueInExpKey = getExperimentKeyValue(
                decodedExperimentsKey,
                featureFlags.wacWebTrafficAAGate.featureFlagName
            );
            const featureGatesAnonIdUsedInSSR =
                window?.featureGateBootstrapValues.identifiers
                    .analyticsAnonymousId;

            if (
                evaluatedValueInExpKey != null &&
                String(wacWebTrafficAAGateValue) !== evaluatedValueInExpKey
            ) {
                analyticsWebClient.sendOperationalEvent({
                    action: "mismatched",
                    actionSubject: "featureGate",
                    source: "layout",
                    attributes: {
                        featureGateName:
                            featureFlags.wacWebTrafficAAGate.featureFlagName,
                        route: window.location.pathname,
                        anonIdUsedInSSR: featureGatesAnonIdUsedInSSR,
                        anonIdInAnalyticsClient:
                            analyticsWebClient.getAnonymousId(),
                        experimentKeyValue: evaluatedValueInExpKey,
                        featureGateValue: String(wacWebTrafficAAGateValue),
                        fallback: window.IS_STATIC ? "true" : "false"
                    }
                });
            }
        }
    }
};
/* */

export const PageWrapper = (props: PageWrapperProps) => {
    const hasAutoAtlAnalytics =
        process.env["AUTO_ANALYTICS_ENABLED"] === "true";
    const pageViewProperties: { originProduct?: string } = {};
    const { locale, path, utmMedium, utmContent, utmCampaign } = parseRoute(
        props.location
    );
    const atlassianUserId = Identity.getAtlassianUserId();
    useAnchorLocalStorage();

    // Hook to update title and description dynamically on client side
    useDynamicTitleDescription(props.location, props.queryRef);

    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        trackRouteUFOV1PageLoad(path);
        pageViewProperties.originProduct =
            "wac" +
            (window.location.pathname === "/"
                ? ""
                : window.location.pathname.replace(new RegExp("/", "g"), "-"));

        checkFeatureFlagGate(featureFlags.wacWebUseEsModulesScripts);

        // Trigger exposure for the edge lambda cache product discovery feature flag that is checked server side
        checkFeatureFlagGate(featureFlags.edgeLambdaCacheProductDiscovery);

        // AA gate, purely to observe the traffic volume in Statsig for logged in users
        atlassianUserId &&
            checkFeatureFlagGate(featureFlags.wacWebLoggedInTrafficAAGate);

        // Trigger exposure for iOS smart banner feature flag that is checked server side
        if (path === "software/confluence" || path === "software/jira") {
            checkFeatureFlagGate(featureFlags.iosSmartBanner);
        }
        if (window.location.pathname === "/fastshift/dashboard") {
            checkFeatureFlagGate(featureFlags.sxpEnableFastshiftPortal);
        }
    }

    const uniformContext = createUniformContext();
    uniformContext.update({
        quirks: {
            name: props.userProfile?.name ?? ""
        }
    });

    /* Use Effect below is temporary code for debugging purposes - will be removed */
    // useEdgeKeyMismatchHook(props.analyticsWebClient, wacWebTrafficAAGateValue);
    useEffect(() => {
        // AA gate, purely to observe the traffic volume in Statsig
        const wacWebTrafficAAGateValue = checkFeatureFlagGate(
            featureFlags.wacWebTrafficAAGate
        );

        if (window.location.pathname === "/fastshift/dashboard") {
            checkFeatureFlagGate(featureFlags.sxpEnableFastshiftPortal);
        }
        checkEdgeCacheKeyMismatch(
            props.analyticsWebClient,
            wacWebTrafficAAGateValue
        );

        // Send operational event when static fallback is detected
        if (window.IS_STATIC && props.analyticsWebClient) {
            props.analyticsWebClient.sendOperationalEvent({
                action: "detected",
                actionSubject: "staticWACFallback",
                source: "page-wrapper-streaming",
                attributes: {
                    route: window.location.pathname,
                    userAgent: props.userAgent,
                    anonymousId: props.analyticsWebClient.getAnonymousId()
                }
            });
        }
    }, [props.analyticsWebClient, props.userAgent]);

    return (
        <OneTrustProvider>
            {process.env["NODE_ENV"] !== "production" && <GridOverlayWrapper />}
            <WacAnalyticsClientProvider
                analyticsWebClient={props.analyticsWebClient ?? null}
                location={props.location}
            >
                <InitUFOV1 />
                <LocationProvider location={props.location}>
                    <UFOSegment name="wac">
                        <SmartReCaptcha>
                            <BxpWebStargateClientProvider
                                baseUrl={getStargateBaseUrl()}
                            >
                                <SSRLoginStateProvider
                                    hasCloudSessionTokenCookie={
                                        props.hasCloudSessionTokenCookie ??
                                        false
                                    }
                                    userAgent={props.userAgent}
                                >
                                    <LoggedInUserProvider>
                                        <GupsProvider>
                                            <WAEContextProvider
                                                wacEnvironment={
                                                    process.env[
                                                        "CLIENT_LIBRARY_ENV"
                                                    ] as WACEnvironment
                                                }
                                                uniformEnvironment={getUniformEnvironment(
                                                    typeof window !==
                                                        "undefined"
                                                        ? window.location.href
                                                        : ""
                                                )}
                                                uniformProjectId={
                                                    process.env[
                                                        "UNIFORM_PROJECT_ID"
                                                    ]
                                                }
                                                isCanvas={
                                                    props.isUniformPlayground
                                                }
                                                isAllowPostMessage={
                                                    props.isAllowPostMessage
                                                }
                                                composition={props.composition}
                                                contentfulEnvironment={
                                                    process.env[
                                                        "CONTENTFUL_ENVIRONMENT"
                                                    ] as ContentfulEnvironment
                                                }
                                                personalizationValues={{
                                                    [UserSegmentTypes.CLUSTER_TAG]:
                                                        props.clusterTag
                                                }}
                                                uniformContext={uniformContext}
                                            >
                                                <WacAnalyticsListener>
                                                    <CrossFlowProvider
                                                        locale={locale}
                                                    >
                                                        <SitesContextProvider>
                                                            <UserContextProvider
                                                                location={
                                                                    props
                                                                        .location
                                                                        .href
                                                                }
                                                                referrer={
                                                                    props.referrer
                                                                }
                                                                meResponse={
                                                                    props.userProfile
                                                                }
                                                                meRequest={
                                                                    props.userProfileStatus
                                                                }
                                                                userProfileResponse={
                                                                    props.userContext
                                                                }
                                                                userProfileRequest={
                                                                    props.userContextStatus
                                                                }
                                                                loggedOutContext={
                                                                    props.loggedOutContext
                                                                }
                                                                loggedOutContextStatus={
                                                                    props.loggedOutContextStatus
                                                                }
                                                                loggedInContext={
                                                                    props.loggedInContext
                                                                }
                                                                loggedInContextStatus={
                                                                    props.loggedInContextStatus
                                                                }
                                                                transactionAccounts={
                                                                    props.transactionAccounts
                                                                }
                                                            >
                                                                <props.PageComponent
                                                                    {...props}
                                                                    utmMedium={
                                                                        utmMedium
                                                                    }
                                                                    utmContent={
                                                                        utmContent
                                                                    }
                                                                    utmCampaign={
                                                                        utmCampaign
                                                                    }
                                                                />
                                                            </UserContextProvider>
                                                        </SitesContextProvider>
                                                        {/**
                                                         * InitializeAutoAtlAnalytics is used to auto-capture GTM events (previously handled
                                                         * by the atl-analytics GTM wrapper). The contents of that wrapper have been ported into
                                                         * this repository.
                                                         *
                                                         * Note: this will only attach auto-analytics to elements that exist in the page
                                                         * when this component is mounted, so it needs to be the last component to render.
                                                         *
                                                         * In its current state, this will only pick up the components that existed on
                                                         * after the first render of the above Component.
                                                         *
                                                         * You can call Initializer.autoTrack() in a useEffect or useLayoutEffect
                                                         * to attach listeners to any new buttons/links/etc. that were added dynamically.
                                                         */}
                                                        {hasAutoAtlAnalytics && (
                                                            <InitializeAutoAtlAnalytics
                                                                pageViewProperties={
                                                                    pageViewProperties
                                                                }
                                                            />
                                                        )}
                                                    </CrossFlowProvider>
                                                </WacAnalyticsListener>
                                            </WAEContextProvider>
                                        </GupsProvider>
                                    </LoggedInUserProvider>
                                </SSRLoginStateProvider>
                            </BxpWebStargateClientProvider>
                        </SmartReCaptcha>
                    </UFOSegment>
                </LocationProvider>
            </WacAnalyticsClientProvider>
        </OneTrustProvider>
    );
};
